import React, { forwardRef, Fragment, useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCRefModalProps, RCCServicesApi, RCCHTMLTextEditor, RCCInput, RCCSelectOptionsCard } from "react-common-components";
import { useForm } from "react-hook-form";
import Title from "src/components/Title";
import Modal from "src/components/Modal";
import Audit from "src/components/Audit";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import DateTimePickerRange from "src/components/DateTimePickerRange";

import Select from "src/components/Select";
import { ILanguageOptionsAvailable, languagesAvailable } from "src/context/Translations/interface";
import { IComponentNotificationsModals, INotifications, IRefNotificationsModals, NotificationsDisplayTo, NotificationsType } from "./interface";
import notificationsSchema from "./schemas";

export const getMessageValue = (defaultValue: string, msg: ILanguageOptionsAvailable) => {
	if(!defaultValue || defaultValue.length === 0)
		return "";
	try{
		const defaultValueAsObj = JSON.parse(defaultValue);
		return defaultValueAsObj[msg];
	}catch(err){
		return defaultValue;
	}
};

const ModalNotifications: React.ForwardRefRenderFunction<IRefNotificationsModals, IComponentNotificationsModals> = ({
	paginationRef
}, ref) => {
	const { translate, currentLanguage } = useTranslation();
	const {addToast} = RCCHookToast();
	const {confirm} = useConfirmModal();

	const [selectedNotification, setSelectedNotification] = useState<INotifications>({efn001Tipo: 1} as INotifications);

	const actionButtonDelete = (row: INotifications) => {
		confirm(
			translate("Exclusão da notificação"),
			`${translate("Deseja realmente excluir a notificação")}: ${row.efn001Id} - ${getMessageValue(row.efn001Msgsimples, currentLanguage)} ?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "Notifications/Delete",
					urlParameters: {
						id: row.efn001Id
					}
				});
	
				addToast({
					color: "green",
					description: translate("Notificação excluída com sucesso"),
					title: translate("Sucesso")
				});

				paginationRef.current?.reload();
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir notificação"), addToast);
			},
			true
		);
	};
	
	// Modal Add/Change
	const [isAdd, setIsAdd] = useState(false);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const { register: registerAddOrChange, control: controlAddOrChange, handleSubmit : handleSubmitAddOrChange, reset : resetAddOrChange, setError : setErrorAddOrChange, formState: { errors : errorsAddOrChange }, clearErrors: clearErrorsAddOrChange } = useForm();
	const submitAddOrChange = async (data: any) => {
		try{
			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();

			const dataChange = {
				...data,
				efn001Mostrar: data.efn001Mostrar ? data.efn001Mostrar.value : ""
			};

			await notificationsSchema(translate).validate(dataChange, {
				abortEarly: false
			});

			if(typeof data.efn001Msgsimples === "object")
				dataChange.efn001Msgsimples = JSON.stringify(data.efn001Msgsimples);

			if(typeof data.efn001Msgcompleta === "object")
				dataChange.efn001Msgcompleta = JSON.stringify(data.efn001Msgcompleta);
			
			if(isAdd){
				await RCCServicesApi.postRequest({
					method: "Notifications/Insert",
					body: {
						...dataChange
					}
				});
			}else{
				await RCCServicesApi.putRequest({
					method: "Notifications/Update",
					body: {
						...dataChange,
						efn001Id: selectedNotification.efn001Id
					}
				});
			}

			addToast({
				color: "green",
				description: isAdd ? translate("Notificação adicionada com sucesso") : translate("Notificação alterada com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
			paginationRef.current?.reload();

		}catch(err){

			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange))
				return;

			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar notificação") : translate("Erro ao alterar notificação"), addToast);
		}
	};

	const selectMostrar = (efn001Mostrar: any) => {
		switch (efn001Mostrar) {
		case NotificationsDisplayTo.ALL_USERS:
			return {
				value: String(NotificationsDisplayTo.ALL_USERS),
				label: translate("Todos Usuários")
			};
		case NotificationsDisplayTo.ADMINISTRATORS:
			return {
				value: String(NotificationsDisplayTo.ADMINISTRATORS),
				label: translate("Administradores")
			};
		case NotificationsDisplayTo.NO_ADMINISTRATORS:
			return {
				value: String(NotificationsDisplayTo.NO_ADMINISTRATORS),
				label: translate("Não Administradores")
			};
		default:
			return {
				value: "",
				label: ""
			};
		}
	};

	const actionButtonAdd = () => {
		resetAddOrChange(); 
		setSelectedNotification({efn001Tipo: 1} as INotifications); 
		setIsAdd(true); 
		modalAddOrChange.current?.openModal();
	};
	const actionButtonChange = (row: INotifications) => {
		resetAddOrChange(); 
		setSelectedNotification(row); 
		setIsAdd(false); 
		modalAddOrChange.current?.openModal(); 
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));
    
	return (
		<>
			{/* Modal ADD/CHANGE */}
			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Adicionar notificação") : translate("Alterar notificação"),
					icon: {
						name: "infoCircle"
					}
				}}
				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
			>
				<RCCGrid
					container
					item
					direction="column"
					alignItems="center"
				>
					<RCCGrid
						container
						item
						sm={10}
						alignItems="center"
						justify="space-between"
					>
						{
							selectedNotification.efn001Id &&
							<Title color="purple" size="sm">
								{`${translate("Código")}: ${selectedNotification.efn001Id}`}
							</Title>
						}
						<Audit data={selectedNotification} type="pop"/>
					</RCCGrid>
					
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
						justifyContent="flex-start"
						direction="column"
						alignItems="flex-start"
					>
						<RCCGrid
							container
							item
							xs={12}
							sm={4}
						>
							<RCCSelectOptionsCard
								name="efn001Tipo"
								control={controlAddOrChange}								
								defaultValue={selectedNotification.efn001Tipo ? String(selectedNotification.efn001Tipo) : "1"}
								options={[
									{
										value: String(NotificationsType.SUCCESS),
										label:translate("Sucesso")
									},
									{
										value: String(NotificationsType.INFORMATION),
										label:translate("Informação")
									},
									{
										value: String(NotificationsType.WARNING),
										label:translate("Alerta")
									},
									{
										value: String(NotificationsType.ERROR),
										label:translate("Erro")
									}
								]}
								label={translate("Tipo")}
							/>
						</RCCGrid>
						<RCCGrid
							container
							item
							direction="row"
							spacing={1}
						>
							<RCCGrid
								container 
								item
								xs={12}
								sm={6}
							>
								<Select 
									control={controlAddOrChange}
									name="efn001Mostrar" 
									options={[
										{value: String(NotificationsDisplayTo.ALL_USERS), label: translate("Todos Usuários")}, 
										{value: String(NotificationsDisplayTo.ADMINISTRATORS), label: translate("Administradores")},
										{value: String(NotificationsDisplayTo.NO_ADMINISTRATORS), label: translate("Não Administradores")}
									]} 
									label={translate("Exibir para")}
									defaultValue={selectedNotification.efn001Mostrar ? selectMostrar(selectedNotification.efn001Mostrar) : {value: String(NotificationsDisplayTo.ALL_USERS), label: translate("Todos Usuários")}}
									placeholder={translate("Selecionar Opção")}
									submit="value and label"
									error={errorsAddOrChange.efn001Mostrar}
									required
								/>
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={6}
							>
								<DateTimePickerRange
									control={controlAddOrChange}
									label={translate("Período")}
									labelSeparator={translate("até")}
									nameInit = "efn001Dtinicial"
									nameEnd = "efn001Dtfinal"
									defaultValueInit={selectedNotification.efn001Dtinicial ? selectedNotification.efn001Dtinicial : new Date()}
									defaultValueEnd={selectedNotification.efn001Dtfinal ? selectedNotification.efn001Dtfinal : new Date()}
									error={errorsAddOrChange.efn001Dtinicial || errorsAddOrChange.efn001Dtfinal}
									type="datePicker"
									required
								/>
							</RCCGrid>
						</RCCGrid>
						{languagesAvailable.map((msg: ILanguageOptionsAvailable) => 
							<Fragment key={msg}>
								<hr />
								<Title color="purple">
									{msg === "en" && translate("Inglês")}
									{msg === "pt" && translate("Português")}
									{msg === "es" && translate("Espanhol")}
								</Title>
								<RCCGrid
									container
									item
									xs={12}
								>
									<RCCInput
										name={`efn001Msgsimples.${msg}`} 
										label={translate("Título")}
										register={registerAddOrChange}
										maxLength={4000}
										defaultValue={getMessageValue(selectedNotification.efn001Msgsimples, msg)}
										error={errorsAddOrChange.efn001Msgsimples && errorsAddOrChange.efn001Msgsimples[msg]}
										required
									/>
								</RCCGrid>
								<RCCGrid
									container
									item
									xs={12}
								>
									<RCCHTMLTextEditor
										name={`efn001Msgcompleta.${msg}`}
										label={translate("Mensagem")}
										control={controlAddOrChange}
										defaultValue={getMessageValue(selectedNotification.efn001Msgcompleta, msg)}
										error={errorsAddOrChange.efn001Msgcompleta && errorsAddOrChange.efn001Msgcompleta[msg]}
										required
									/>
								</RCCGrid>
							</Fragment>
						)}
					</RCCGrid>
				</RCCGrid>
			</Modal>
		</>
	);
};

export default forwardRef(ModalNotifications);
